import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';

const DonePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const result = location.state?.result;
  const [copied, setCopied] = useState(false);
  const [thumbError, setThumbError] = useState(false);

  if (!result) {
    navigate('/');
    return null;
  }

  const downloadUrl = `${window.location.origin}${result.downloadUrl}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(downloadUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col overflow-x-hidden">
      <Header />

      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-3xl flex flex-col gap-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="relative w-24 h-24 bg-surface-dark rounded-2xl shadow-2xl flex items-center justify-center overflow-hidden border border-surface-border">
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 text-green-500 mb-2 ring-1 ring-green-500/20">
              <span className="material-symbols-outlined text-[32px]">check_circle</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900 dark:text-white">
              Gotowe! Twój link jest przygotowany.
            </h1>
            <p className="text-slate-600 dark:text-gray-400 text-lg max-w-xl mx-auto">
              Konwersja zakończona pomyślnie. Skopiuj poniższy link i wykorzystaj go w grze, aby
              odtwarzać muzykę.
            </p>
          </div>

          {/* Result Card */}
          <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-200 dark:border-surface-border shadow-xl overflow-hidden relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-purple-600 rounded-2xl opacity-20 blur group-hover:opacity-30 transition duration-1000 group-hover:duration-200 pointer-events-none"></div>
            <div className="relative p-6 sm:p-8 flex flex-col gap-8 bg-white dark:bg-surface-dark rounded-2xl">
              {/* Song Info */}
              <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                {/* Thumbnail */}
                <div className="w-full sm:w-48 aspect-video rounded-xl shadow-lg ring-1 ring-black/10 dark:ring-white/10 overflow-hidden flex items-center justify-center bg-gradient-to-br from-primary/20 to-purple-600/20">
                  {result.thumbnailUrl && !thumbError ? (
                    <img
                      src={result.thumbnailUrl}
                      alt={result.title}
                      className="w-full h-full object-cover"
                      onError={() => setThumbError(true)}
                    />
                  ) : (
                    <span className="material-symbols-outlined text-primary text-5xl">music_note</span>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 space-y-2 min-w-0 w-full">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white leading-tight truncate pr-4">
                        {result.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-500 dark:text-gray-400">
                        <span className="material-symbols-outlined text-[16px]">schedule</span>
                        <span>{formatDuration(result.duration)}</span>
                        {result.renewed && (
                          <>
                            <span className="mx-1">•</span>
                            <span className="text-green-500">Odnowiono</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full mt-4 overflow-hidden">
                    <div className="h-full bg-primary w-full rounded-full"></div>
                  </div>
                  <div className="flex justify-between text-xs font-medium text-primary mt-1">
                    <span>Gotowe</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>

              {/* Copy Section */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700 dark:text-gray-300 ml-1">
                  Twój link bezpośredni
                </label>
                <div className="flex shadow-sm rounded-lg overflow-hidden group/input">
                  <div className="relative flex-grow focus-within:z-10">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-slate-400 dark:text-gray-500 text-[20px]">
                        link
                      </span>
                    </div>
                    <input
                      className="block w-full rounded-l-lg border-0 py-3.5 pl-10 text-slate-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-surface-border placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary bg-slate-50 dark:bg-[#151b28] sm:text-sm sm:leading-6 font-mono"
                      type="text"
                      value={downloadUrl}
                      readOnly
                    />
                  </div>
                  <button
                    onClick={handleCopy}
                    className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-lg px-6 py-2 text-sm font-bold text-white shadow-sm hover:bg-blue-600 bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-all active:scale-[0.98]"
                    type="button"
                  >
                    <span className="material-symbols-outlined text-[20px]">
                      {copied ? 'check' : 'content_copy'}
                    </span>
                    <span className="hidden sm:inline">{copied ? 'Skopiowano!' : 'Skopiuj'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-2">
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-300 dark:border-surface-border text-slate-700 dark:text-white bg-transparent hover:bg-slate-100 dark:hover:bg-surface-border font-medium transition-all group"
            >
              <span className="material-symbols-outlined group-hover:-translate-y-0.5 transition-transform">
                autorenew
              </span>
              Konwertuj kolejny utwór
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DonePage;
